import store from '../store/store';
import {
	callAudioUploadFailed,
	callAudioUploadFinishing,
	callAudioUploadStarted,
	callAudioUploadSucceeded,
	queueCallAudioUploads
} from '../store/actions/callAudioUploadActions';
import { getUid } from './ids';
import {
	CallAudioUploadCallIdentifier,
	CallAudioUploadDescriptor,
	CallAudioUploadFailureDescriptor
} from '../store/state/callAudioUploadState';
import api from './api';
import {
	AxiosError,
	AxiosProgressEvent,
	AxiosRequestConfig
} from 'axios';
import { progressBarCompletingPause } from './constants';
import { isSupportedAudioFileType } from './utilities';
import { CallDetails } from '../models/dto/call';
import { toast } from 'react-toastify';
import { doCallAudioIdentifiersMatch } from './jobHelpers';
import orderBy from 'lodash/orderBy';

class CallAudioUploader {
	private MAX_CONCURRENT_UPLOADS = 3;
	/**
	 * max file size is 200mb (TUP-131)
	 */
	private MAX_FILE_SIZE = 200 * 1000000;
	private filesByUploadId: Map<string, File> = new Map();
	private subs: UploadProgressEventHandler[] = [];
	
	constructor() {
		// fallback in case things go wrong (like exceptions
		// get thrown and keep the next set of uploads from
		// getting kicked off)
		setInterval(() => {
			this.update();
		}, 5000);
	}
	
	uploadFiles(files: File[], jobId: number) {
		const descriptors: CallAudioUploadDescriptor[] = [],
			failureIdsToClear: string[] = [],
			failuresToAdd: CallAudioUploadFailureDescriptor[] = [],
			state = store.getState(),
			job = state.jobs.details.get(jobId);
		
		let duplicateUploadCount = 0;
		
		if (!job)
			return;

		files.forEach(file => {
			const filename = this.getNormalizedFilename(file.name);
			if (!filename) {
				// filename is missing for some reason so we'll just
				// have to ignore this file
				return;
			}
			
			const call = job.calls?.find(c => this.getNormalizedFilename(c.fileName) === filename),
				callIdentifier: CallAudioUploadCallIdentifier = call ? { type: 'call', id: call.id, filename } : { type: 'filename', filename };
			
			if (!call && !job.createdWithoutCallManifest) {
				// we couldn't find a call matching this filename
				failuresToAdd.push({
					uploadId: getUid(),
					filename,
					jobId,
					description: 'No matching call found.',
					callIdentifier,
					failureType: null
				});
				
				return;
			}
			
			if (call && call.tethrCallId) {
				// this file has already been uploaded so we'll just
				// ignore it.
				duplicateUploadCount++;
				return;
			}
			
			const existing = state.callAudioUpload.uploads.find(u => u.jobId === jobId && doCallAudioIdentifiersMatch(u.callIdentifier, callIdentifier, false));
			if (existing) {
				// there is already an upload in progress so we'll
				// just ignore this file.
				duplicateUploadCount++;
				return;
			}
			
			const failure = state.callAudioUpload.failures.find(f => f.jobId === jobId && doCallAudioIdentifiersMatch(f.callIdentifier, callIdentifier, false));
			if (failure) {
				// there is an existing failure we need to clear
				// before starting a new upload.
				failureIdsToClear.push(failure.uploadId);
				this.filesByUploadId.delete(failure.uploadId);
			}
			
			const validation = this.validateFile(file);
			if (validation.type === 'valid') {
				const uploadId = getUid();
				this.filesByUploadId.set(uploadId, file);
				descriptors.push({
					uploadId,
					callIdentifier,
					jobId,
					filename
				});
			} else {
				failuresToAdd.push({
					uploadId: getUid(),
					callIdentifier,
					jobId,
					filename,
					description: validation.description,
					failureType: null
				});
			}
		});
		
		store.dispatch(queueCallAudioUploads(descriptors, failureIdsToClear, failuresToAdd));
		
		this.update();
		
		if (duplicateUploadCount > 0) {
			const formattedCount = duplicateUploadCount.toLocaleString(),
				duplicateMessage = duplicateUploadCount === 1
					? `${formattedCount} file has already been added and was ignored`
					: `${formattedCount} files have already been added and were ignored`;
			
			toast.warn(duplicateMessage);
		}
	}
	
	subscribeToProgress(sub: UploadProgressEventHandler): () => void {
		this.subs.push(sub);
		
		return () => {
			this.subs = this.subs.filter(s => s !== sub);
		}
	}
	
	private update() {
		const state = store.getState();
		const uploading = state.callAudioUpload.uploads.filter(d => d.status === 'uploading');
		const toUploadCount = this.MAX_CONCURRENT_UPLOADS - uploading.length;
		
		if (toUploadCount <= 0)
			return;
		
		const queued = orderBy(state.callAudioUpload.uploads.filter(d => d.status === 'queued'), [u => u.uploadOrder], ['asc']);
		for (let i = 0; i < toUploadCount; i++) {
			const descriptor = queued[i];
			if (!descriptor) {
				// we've reached the end of the queue
				break;
			}
			
			const file = this.filesByUploadId.get(descriptor.uploadId);
			if (!file) {
				// this shouldn't ever happen
				continue;
			}
			
			store.dispatch(callAudioUploadStarted(descriptor.uploadId));
			
			const url = descriptor.callIdentifier.type === 'call' ? `/api/calls/${descriptor.callIdentifier.id}/audio` : `/api/calls/create-with-audio`;
			const config: AxiosRequestConfig = {
				headers: { 'Content-Type': 'multipart/form-data' },
				onUploadProgress: (e: AxiosProgressEvent) => {
					this.subs.forEach(s => s({
						jobId: descriptor.jobId, 
						callIdentifier: descriptor.callIdentifier, 
						progress: e.total != null ? e.loaded / e.total : e.loaded
					}));
				},
				params: descriptor.callIdentifier.type === 'call' ? null : { jobId: descriptor.jobId, filename: descriptor.filename }
			};
			const data = new FormData();
			data.append('audio', file);
			
			api.post<CallDetails>(url, data, config).then(response => {
				store.dispatch(callAudioUploadFinishing(descriptor.uploadId));
				
				// give the progress bar time to finish up
				setTimeout(() => {
					this.filesByUploadId.delete(descriptor.uploadId);
					store.dispatch(callAudioUploadSucceeded(descriptor.uploadId, descriptor.jobId, response.data));
					this.update();
				}, progressBarCompletingPause);
			}, (error: AxiosError) => {
				const status = error.response?.status;
				const isQuotaError = status === 428;
				
				this.filesByUploadId.delete(descriptor.uploadId);
				store.dispatch(callAudioUploadFailed(descriptor.uploadId, this.getAudioUploadFailureDescription(error), isQuotaError ? 'quotaReached' : this.statusCodeIsServerError(status) ? 'serverError' : null));
				this.update();
				
				if (isQuotaError) {
					const state = store.getState();
					const quotaFailures = state.callAudioUpload.failures.filter(f => f.failureType === 'quotaReached');
					if (quotaFailures.length === 1) {
						toast.error('Looks like you’ve used all the minutes or calls within your trial.', { autoClose: 10000 });
					}
				}
			});
		}
	}
	
	private statusCodeIsServerError(status: number | nundefined): boolean {
		return status != null && status >= 500 && status < 600;
	}
	
	private getAudioUploadFailureDescription(error: AxiosError): string {
		if (error.response?.status === 428) {
			return 'No minutes or calls remaining.';
		} else {
			return 'Audio upload failed.';
		}
	}
	
	private getNormalizedFilename(name: string | nundefined): string {
		return (name ?? '').trim();
	}
	
	private validateFile(file: File): { type: 'valid'; } | { type: 'invalid'; description: string; } {
		if (file.size > this.MAX_FILE_SIZE) {
			return { type: 'invalid', description: 'File is too large.' };
		}
		
		if (!isSupportedAudioFileType(file.type)) {
			return { type: 'invalid', description: 'Unsupported file type.' };
		}
		
		return { type: 'valid' };
	}
}

const uploader = new CallAudioUploader();

export default uploader;

interface UploadProgressEvent {
	jobId: number;
	callIdentifier: CallAudioUploadCallIdentifier;
	progress: number;
}

type UploadProgressEventHandler = (event: UploadProgressEvent) => void;